type FbqFn = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: FbqFn;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

export interface MetaCheckoutParams {
  value: number;
  currency: string;
  content_ids: string[];
  content_name: string;
  content_type: 'product';
  num_items: number;
}

// Meta Pixel ids are public (visible in page source); the hardcoded default
// guarantees the production build tracks even when the deploy pipeline does
// not set VITE_PUBLIC_META_PIXEL_ID. Set it to an empty string to disable.
export const DEFAULT_META_PIXEL_ID = '1038515245345580';

const FBEVENTS_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

let initialized = false;

const getPixelId = () =>
  import.meta.env.VITE_PUBLIC_META_PIXEL_ID ?? DEFAULT_META_PIXEL_ID;

export const toMetaContentId = (courseId: string) => `course_${courseId}`;

export const isMetaPixelEnabled = () =>
  Boolean(getPixelId()) &&
  import.meta.env.VITE_PUBLIC_APP_ENV !== 'staging' &&
  import.meta.env.MODE === 'production';

export const initMetaPixel = () => {
  if (initialized || typeof window === 'undefined' || !isMetaPixelEnabled()) {
    return;
  }

  if (!window.fbq) {
    const fbq = ((...args: unknown[]) => {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue.push(args);
      }
    }) as FbqFn;
    fbq.queue = [];
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    window.fbq = fbq;
    window._fbq = fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = FBEVENTS_SRC;
    document.head.appendChild(script);
  }

  window.fbq('init', getPixelId());
  initialized = true;
};

export const trackPageView = () => {
  if (!initialized) return;
  window.fbq?.('track', 'PageView');
};

export const trackInitiateCheckout = (params: MetaCheckoutParams) => {
  initMetaPixel();
  if (!initialized) return;
  window.fbq?.('track', 'InitiateCheckout', params);
};

export const trackPurchase = (params: MetaCheckoutParams, eventId: string) => {
  initMetaPixel();
  if (!initialized) return;
  window.fbq?.('track', 'Purchase', params, { eventID: eventId });
};

/** Test-only: reset module state between tests. */
export const __resetMetaPixelForTests = () => {
  initialized = false;
};
